import { useState, type RefObject } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { MessageCircle, X } from "lucide-react";
import { MeetingChatSidebar } from "./MeetingChatSidebar";
import type { MeetingChatMessage } from "@/types/chat";

interface MobileChatDrawerProps {
  messages: MeetingChatMessage[];
  currentMessage: string;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onReact: (messageId: string, emoji: string) => void;
  onSendFile: (file: File) => Promise<void>;
  uploadingFile?: boolean;
  isOnline?: boolean;
  messagesEndRef?: RefObject<HTMLDivElement>;
}

export const MobileChatDrawer = ({
  messages,
  currentMessage,
  onMessageChange,
  onSendMessage,
  onReact,
  onSendFile,
  uploadingFile,
  isOnline = true,
  messagesEndRef,
}: MobileChatDrawerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="default"
          size="icon"
          className="relative h-14 w-14 rounded-full border border-primary/20 bg-primary shadow-lg shadow-primary/25 transition-transform hover:scale-110"
          title="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
              {messages.length > 99 ? '99+' : messages.length}
            </span>
          )}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="h-[85vh] bg-[linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.18))]">
        <DrawerHeader className="border-b border-border/70 text-left">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="flex items-center text-lg">
                <MessageCircle className="h-5 w-5 mr-2" />
                Chat
              </DrawerTitle>
              <DrawerDescription>
                {messages.length} {messages.length === 1 ? 'message' : 'messages'}
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="h-[70vh] px-4 pb-4 pt-2">
          <MeetingChatSidebar
            messages={messages}
            currentMessage={currentMessage}
            onMessageChange={onMessageChange}
            onSendMessage={onSendMessage}
            onSendReaction={onReact}
            onSendFile={onSendFile}
            uploadingFile={uploadingFile}
            isOnline={isOnline}
            className="h-full border-none shadow-none"
            messagesEndRef={messagesEndRef}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
